import React, { useState, useEffect } from 'react';
import { Dropdown, ButtonGroup } from 'react-bootstrap';
import ButtonWithLoader from './components/ButtonWithLoader';

interface SaveDropdownButtonProps {
    isLoading?:boolean
    enableShortcuts?:boolean
    onSave: (event: React.FormEvent) => void;
    onSaveAndSend: (event: React.FormEvent) => void;
    onSaveAndSendWP?: (event: React.FormEvent) => void;
    onSaveAndPrint: (event: React.FormEvent) => void;
    onSaveAsDraft?: (event: React.FormEvent) => void;
    onSaveAndAddNew?: (event: React.FormEvent) => void;
}

// ADD: Type for save actions
type SaveAction = 'save' | 'saveAndSend' | 'saveAndSendWP' | 'saveAndPrint' | 'saveAsDraft' | 'saveAndAddNew';

// ADD: LocalStorage key constant
const LAST_SAVE_ACTION_KEY = 'myBooks_lastSaveAction';

export const SaveDropdownButton: React.FC<SaveDropdownButtonProps> = ({
  isLoading,
  enableShortcuts,
  onSave,
  onSaveAndSend,
  onSaveAndSendWP,
  onSaveAndPrint,
  onSaveAsDraft,
  onSaveAndAddNew,
}) => {
  // ADD: State to track default action
  const [defaultAction, setDefaultAction] = useState<SaveAction>('save');

  // ADD: Load last action from localStorage on mount
  useEffect(() => {
    const lastAction = localStorage.getItem(LAST_SAVE_ACTION_KEY) as SaveAction;
    if (lastAction) {
      setDefaultAction(lastAction);
    }
  }, []);

  // ADD: Handler to save action and execute
  const handleAction = (action: SaveAction, handler: (event: React.FormEvent) => void) => {
    return (event: React.FormEvent) => {
      localStorage.setItem(LAST_SAVE_ACTION_KEY, action);
      setDefaultAction(action);
      handler(event);
    };
  };

  // ADD: Get button config based on default action
  const getDefaultButtonConfig = () => {
    switch (defaultAction) {
      case 'saveAndSendWP':
        return {
          icon: 'fab fa-whatsapp',
          text: 'Save & Send Whatsapp'.toTranslatedDisplay(),
          handler: onSaveAndSendWP || onSave,
        };
      case 'saveAndSend':
        return {
          icon: 'bi bi-send',
          text: 'Save and Send'.toTranslatedDisplay(),
          handler: onSaveAndSend,
        };
      case 'saveAndPrint':
        return {
          icon: 'bi bi-printer',
          text: 'Save and Print'.toTranslatedDisplay(),
          handler: onSaveAndPrint,
        };
      case 'saveAsDraft':
        return {
          icon: 'bi bi-file-earmark',
          text: 'Save as Draft'.toTranslatedDisplay(),
          handler: onSaveAsDraft || onSave,
        };
      case 'saveAndAddNew':
        return {
          icon: 'bi bi-plus-circle',
          text: 'Save & Add New'.toTranslatedDisplay(),
          handler: onSaveAndAddNew || onSave,
        };
      default:
        return {
          icon: 'bi bi-check-circle',
          text: 'Save'.toTranslatedDisplay(),
          handler: onSave,
        };
    }
  };

  const shortcutKeys = (enableShortcuts) ? ['ctrl+s', 'meta+s'] : [];
  const buttonConfig = getDefaultButtonConfig();

  return (
    <Dropdown as={ButtonGroup}>
      {/* MODIFIED: Dynamic button based on last action */}
      <ButtonWithLoader 
        shortcutTip='Ctrl+S / ⌘+S' 
        shortcutKeys={shortcutKeys} 
        isLoading={isLoading||false} 
        onClick={handleAction(defaultAction, buttonConfig.handler)} 
        className="btn btn-primary no-right-rounded "
      >
        <i className={buttonConfig.icon}></i> {buttonConfig.text}
      </ButtonWithLoader>
      <div className="btn-splitter border border-primary-light"></div>
      <Dropdown.Toggle split variant="primary" id="dropdown-split-basic" className="fs-3 btn-sm" />

      <Dropdown.Menu>
        {/* MODIFIED: All dropdown items now use handleAction */}
        <Dropdown.Item onClick={handleAction('save', onSave)}>
          <i className="bi bi-check-circle me-2"></i> {'Save'.toTranslatedDisplay()}
        </Dropdown.Item>
        <Dropdown.Item onClick={handleAction('saveAndSend', onSaveAndSend)}>
          <i className="bi bi-send me-2"></i> {'Save and Send'.toTranslatedDisplay()}
        </Dropdown.Item>
        <Dropdown.Item onClick={handleAction('saveAndSendWP', onSaveAndSendWP || onSave)}>
          <i className="fab fa-whatsapp me-2"></i> {'Save & Send Whatsapp'.toTranslatedDisplay()}
        </Dropdown.Item>
        <Dropdown.Item onClick={handleAction('saveAndPrint', onSaveAndPrint)}>
          <i className="bi bi-printer me-2"></i> {'Save and Print'.toTranslatedDisplay()}
        </Dropdown.Item>
        <Dropdown.Item onClick={handleAction('saveAsDraft', onSaveAsDraft || onSave)}>
          <i className="bi bi-file-earmark me-2"></i> {'Save as Draft'.toTranslatedDisplay()}
        </Dropdown.Item>
        {onSaveAndAddNew && (
          <Dropdown.Item onClick={handleAction('saveAndAddNew', onSaveAndAddNew)}>
            <i className="bi bi-plus-circle me-2"></i> {'Save & Add New'.toTranslatedDisplay()}
          </Dropdown.Item>
        )}
      </Dropdown.Menu>
    </Dropdown>
  );
};